(() => {
  const SUPPORTED_DISPLAY_CURRENCIES = new Set(['EUR', 'USD', 'CNY']);
  const currencyStorageKey = 'jjga-display-currency';
  const preferredCurrency = () => {
    try {
      const value = window.sessionStorage.getItem(currencyStorageKey);
      return SUPPORTED_DISPLAY_CURRENCIES.has(value) ? value : 'EUR';
    } catch {
      return 'EUR';
    }
  };
  const syncCurrencyLabels = (value) => {
    if (!SUPPORTED_DISPLAY_CURRENCIES.has(value)) return;
    document.querySelectorAll('[data-jj-header-currency]').forEach((label) => {
      label.textContent = value;
    });
  };
  const rememberCurrency = (value) => {
    if (!SUPPORTED_DISPLAY_CURRENCIES.has(value)) return;
    try { window.sessionStorage.setItem(currencyStorageKey, value); } catch (_) {}
    syncCurrencyLabels(value);
  };
  const safeMinor = (value) => {
    const numeric = Number.parseInt(String(value || ''), 10);
    return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : null;
  };
  const ratio = (value, fallback) => {
    const numeric = Number.parseInt(String(value || ''), 10);
    return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : fallback;
  };
  const convertMinor = (amount, numerator, denominator) => {
    if (!Number.isSafeInteger(amount) || amount < 0 || !Number.isSafeInteger(numerator)
      || numerator <= 0 || !Number.isSafeInteger(denominator) || denominator <= 0) return null;
    const converted = Math.floor((amount * denominator + Math.floor(numerator / 2)) / numerator);
    return Number.isSafeInteger(converted) && converted >= 0 ? converted : null;
  };
  const formatMinor = (amount, currency) => {
    if (!Number.isSafeInteger(amount) || amount < 0 || !SUPPORTED_DISPLAY_CURRENCIES.has(currency)) return null;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency', currency, currencyDisplay: 'symbol', minimumFractionDigits: 2,
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${currency}`;
    }
  };

  const header = document.querySelector('[data-site-header]');

  syncCurrencyLabels(preferredCurrency());

  const syncHeader = () => {
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 24);
  };

  if (header && 'IntersectionObserver' in window) {
    const sentinel = document.createElement('span');
    sentinel.className = 'jj-header-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    header.before(sentinel);
    const headerObserver = new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-scrolled', !entry.isIntersecting);
    }, {threshold: 1});
    headerObserver.observe(sentinel);
  } else {
    syncHeader();
  }

  document.querySelectorAll('.jj-mobile-menu a').forEach((link) => {
    link.addEventListener('click', () => link.closest('details')?.removeAttribute('open'));
  });

  const disclosureMenus = [...document.querySelectorAll('.jj-language-switcher, .jj-mobile-menu')];

  disclosureMenus.forEach((menu) => {
    menu.addEventListener('toggle', () => {
      if (!menu.open) return;
      disclosureMenus.forEach((otherMenu) => {
        if (otherMenu !== menu) otherMenu.removeAttribute('open');
      });
    });
  });

  document.addEventListener('click', (event) => {
    disclosureMenus.forEach((menu) => {
      if (menu.open && !menu.contains(event.target)) menu.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    disclosureMenus.forEach((menu) => menu.removeAttribute('open'));
  });

  document.querySelectorAll('[data-jj-vehicle-gallery]').forEach((gallery) => {
    const mainImage = gallery.querySelector('[data-jj-vehicle-gallery-main]');
    const thumbnails = [...gallery.querySelectorAll('[data-jj-vehicle-gallery-thumb]')];
    const counter = gallery.querySelector('[data-jj-vehicle-gallery-counter]');
    if (!mainImage || !thumbnails.length) return;

    const selectImage = (thumbnail) => {
      const source = thumbnail.dataset.imageSrc;
      if (!source) return;
      mainImage.src = source;
      mainImage.removeAttribute('srcset');
      mainImage.alt = thumbnail.dataset.imageAlt || mainImage.alt;
      thumbnails.forEach((item) => {
        const active = item === thumbnail;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (counter) counter.textContent = `${thumbnails.indexOf(thumbnail) + 1} / ${thumbnails.length}`;
      thumbnail.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => selectImage(thumbnail));
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active')));
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (activeIndex + direction + thumbnails.length) % thumbnails.length;
      event.preventDefault();
      thumbnails[nextIndex].focus();
      selectImage(thumbnails[nextIndex]);
    });
  });

  document.querySelectorAll('[data-jj-vehicle-marketplace]').forEach((marketplace) => {
    const form = marketplace.querySelector('[data-jj-vehicle-filters]');
    const nameControl = marketplace.querySelector('[data-jj-vehicle-name]');
    const nameOptions = marketplace.querySelector('[data-jj-vehicle-name-options]');
    const brandControl = marketplace.querySelector('[data-jj-vehicle-brand]');
    const budgetControl = marketplace.querySelector('[data-jj-vehicle-source-budget]');
    const currencyControl = marketplace.querySelector('[data-jj-vehicle-currency]');
    const budgetCurrency = marketplace.querySelector('[data-jj-budget-currency]');
    const result = marketplace.querySelector('[data-jj-vehicle-filter-result]');
    const noMatches = marketplace.querySelector('[data-jj-vehicle-filter-empty]');
    const pagination = marketplace.querySelector('[data-jj-vehicle-pagination]');
    const previousPageControl = marketplace.querySelector('[data-jj-vehicle-page-previous]');
    const nextPageControl = marketplace.querySelector('[data-jj-vehicle-page-next]');
    const pageList = marketplace.querySelector('[data-jj-vehicle-page-list]');
    const pageLabel = marketplace.querySelector('[data-jj-vehicle-page-label]');
    const cards = [...marketplace.querySelectorAll('[data-jj-vehicle-card]')];
    if (!form || !nameControl || !brandControl || !budgetControl || !currencyControl || !result || !noMatches || !cards.length) return;
    currencyControl.value = preferredCurrency();
    const eurUsdNumerator = ratio(marketplace.dataset.eurUsdNumerator, 10000);
    const eurUsdDenominator = ratio(marketplace.dataset.eurUsdDenominator, 11664);
    const parsedPageSize = Number.parseInt(marketplace.dataset.jjVehiclePageSize || '', 10);
    const pageSize = Number.isSafeInteger(parsedPageSize) && parsedPageSize > 0 ? parsedPageSize : 10;
    let currentPage = 1;
    const searchLocale = document.documentElement.lang || 'es';
    const normalizeVehicleSearch = (value) => String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase(searchLocale)
      .replace(/\s+/g, ' ')
      .trim();

    if (nameOptions) {
      const localizedTitles = [...new Set(cards
        .map((card) => (card.dataset.jjVehicleTitle || '').trim())
        .filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, searchLocale));
      localizedTitles.forEach((title) => {
        const option = document.createElement('option');
        option.value = title;
        nameOptions.append(option);
      });
    }

    const brands = new Map();
    cards.forEach((card) => {
      const key = (card.dataset.brand || '').trim();
      const label = (card.dataset.brandLabel || key).trim();
      if (key && label) brands.set(key, label);
    });
    [...brands.entries()]
      .sort(([, left], [, right]) => left.localeCompare(right, document.documentElement.lang || 'es'))
      .forEach(([key, label]) => {
        const option = document.createElement('option');
        option.value = key;
        option.textContent = label;
        brandControl.append(option);
      });

    const updateFieldStates = () => {
      nameControl.closest('.jj-vehicle-marketplace__field')?.classList.toggle('is-populated', nameControl.value.trim() !== '');
      brandControl.closest('.jj-vehicle-marketplace__field')?.classList.toggle('is-populated', Boolean(brandControl.value));
      currencyControl.closest('.jj-vehicle-marketplace__field')?.classList.toggle('is-populated', Boolean(currencyControl.value));
      budgetControl.closest('.jj-vehicle-marketplace__field')?.classList.toggle('is-populated', budgetControl.value.trim() !== '');
    };

    const focusCurrentPageControl = () => {
      const currentPageControl = pageList?.querySelector('[aria-current="page"]');
      currentPageControl?.focus({ preventScroll: true });
    };

    const renderPagination = (totalPages, shouldFocus) => {
      if (!pagination || !previousPageControl || !nextPageControl || !pageList) return;
      pagination.hidden = totalPages <= 1;
      previousPageControl.disabled = currentPage <= 1;
      nextPageControl.disabled = currentPage >= totalPages;
      if (pageLabel) {
        const pageLabelPrefix = pageLabel.dataset.prefix || 'Page';
        const pageLabelSuffix = pageLabel.dataset.suffix || '';
        pageLabel.textContent = `${pageLabelPrefix} ${currentPage}${pageLabelSuffix ? ` ${pageLabelSuffix}` : ''}`;
      }
      pageList.replaceChildren();

      for (let page = 1; page <= totalPages; page += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'jj-vehicle-marketplace__page-number';
        button.textContent = String(page);
        button.dataset.jjVehiclePage = String(page);
        const pageLabelPrefix = pageLabel?.dataset.prefix || 'Page';
        const pageLabelSuffix = pageLabel?.dataset.suffix || '';
        button.setAttribute('aria-label', `${pageLabelPrefix} ${page}${pageLabelSuffix ? ` ${pageLabelSuffix}` : ''}`);
        if (page === currentPage) {
          button.setAttribute('aria-current', 'page');
        }
        pageList.append(button);
      }

      if (shouldFocus && totalPages > 1) focusCurrentPageControl();
    };

    const updateResults = ({ resetPage = false, focusPagination = false } = {}) => {
      const selectedVehicleQuery = normalizeVehicleSearch(nameControl.value);
      const selectedBrand = brandControl.value;
      const selectedCurrency = SUPPORTED_DISPLAY_CURRENCIES.has(currencyControl.value) ? currencyControl.value : 'EUR';
      rememberCurrency(selectedCurrency);
      if (budgetCurrency) budgetCurrency.textContent = selectedCurrency;
      const requestedBudget = Number.parseFloat(budgetControl.value);
      const hasBudget = Number.isFinite(requestedBudget) && requestedBudget >= 0;
      const matchedCards = [];

      cards.forEach((card) => {
        const vehicleNameMatches = !selectedVehicleQuery
          || normalizeVehicleSearch(card.dataset.jjVehicleSearchText || card.dataset.jjVehicleTitle).includes(selectedVehicleQuery);
        const brandMatches = !selectedBrand || card.dataset.brand === selectedBrand;
        const cnyMinor = safeMinor(card.dataset.sourcePriceCnyMinor);
        const eurMinor = safeMinor(card.dataset.sourcePriceEurMinor);
        const selectedMinor = selectedCurrency === 'CNY'
          ? cnyMinor
          : selectedCurrency === 'USD'
            ? (eurMinor === null ? null : convertMinor(eurMinor, eurUsdNumerator, eurUsdDenominator))
            : eurMinor;
        const sourcePrice = selectedMinor === null ? null : selectedMinor / 100;
        const budgetMatches = !hasBudget || (sourcePrice !== null && sourcePrice <= requestedBudget);
        const isMatched = vehicleNameMatches && brandMatches && budgetMatches;
        const priceOutput = card.querySelector('[data-jj-source-price-display]');
        const formatted = selectedMinor === null ? null : formatMinor(selectedMinor, selectedCurrency);
        if (priceOutput && formatted) priceOutput.textContent = formatted;
        if (isMatched) matchedCards.push(card);
      });

      const visible = matchedCards.length;
      const totalPages = Math.max(1, Math.ceil(visible / pageSize));
      if (resetPage) currentPage = 1;
      currentPage = Math.min(Math.max(1, currentPage), totalPages);
      const pageStart = (currentPage - 1) * pageSize;
      const pageEnd = pageStart + pageSize;
      cards.forEach((card) => { card.hidden = true; });
      matchedCards.slice(pageStart, pageEnd).forEach((card) => { card.hidden = false; });

      const resultTemplate = visible === 1
        ? result.dataset.templateOne || result.dataset.template
        : result.dataset.template;
      result.textContent = resultTemplate
        ? (resultTemplate.includes('{count}')
          ? resultTemplate.replace('{count}', String(visible))
          : `${visible} ${resultTemplate}`)
        : String(visible);
      noMatches.hidden = visible !== 0;
      updateFieldStates();
      renderPagination(totalPages, focusPagination);
    };

    result.dataset.template = result.textContent || '{count}';
    form.addEventListener('input', () => updateResults({ resetPage: true }));
    form.addEventListener('change', () => updateResults({ resetPage: true }));
    form.addEventListener('reset', () => window.setTimeout(() => updateResults({ resetPage: true }), 0));
    previousPageControl?.addEventListener('click', () => {
      if (currentPage <= 1) return;
      currentPage -= 1;
      updateResults({ focusPagination: true });
    });
    nextPageControl?.addEventListener('click', () => {
      const matchedCount = cards.filter((card) => !card.hidden).length;
      if (matchedCount === 0) return;
      currentPage += 1;
      updateResults({ focusPagination: true });
    });
    pageList?.addEventListener('click', (event) => {
      const target = event.target.closest('[data-jj-vehicle-page]');
      const requestedPage = Number.parseInt(target?.dataset.jjVehiclePage || '', 10);
      if (!Number.isSafeInteger(requestedPage) || requestedPage === currentPage) return;
      currentPage = requestedPage;
      updateResults({ focusPagination: true });
    });
    updateResults();
  });

  document.querySelectorAll('[data-jj-vehicle-pricing]').forEach((pricing) => {
    const currencyControl = pricing.querySelector('[data-jj-quote-currency-display]');
    const destinationControl = pricing.querySelector('[data-jj-service-destination]');
    const destinationPostalControl = pricing.querySelector('[data-jj-service-postal]');
    const routeScopeOutput = pricing.querySelector('[data-jj-service-route-scope]');
    const routeReviewOutput = pricing.querySelector('[data-jj-service-route-review]');
    const profileConfigElement = pricing.querySelector('[data-jj-service-profiles]');
    if (!currencyControl) return;

    const sourceEurMinor = safeMinor(pricing.dataset.sourcePriceEurMinor);
    const sourceCnyMinor = safeMinor(pricing.dataset.sourcePriceCnyMinor);
    const taxEurMinor = safeMinor(pricing.dataset.taxEurMinor);
    const totalEurMinor = safeMinor(pricing.dataset.totalEurMinor);
    const cnyPerEurNumerator = ratio(pricing.dataset.cnyPerEurNumerator, 78414);
    const cnyPerEurDenominator = ratio(pricing.dataset.cnyPerEurDenominator, 10000);
    const eurUsdNumerator = ratio(pricing.dataset.eurUsdNumerator, 10000);
    const eurUsdDenominator = ratio(pricing.dataset.eurUsdDenominator, 11664);

    const cnyToEur = (amount) => convertMinor(amount, cnyPerEurNumerator, cnyPerEurDenominator);
    const eurToCny = (amount) => convertMinor(amount, cnyPerEurDenominator, cnyPerEurNumerator);
    const eurToUsd = (amount) => convertMinor(amount, eurUsdNumerator, eurUsdDenominator);
    const sourceEur = sourceEurMinor ?? (sourceCnyMinor === null ? null : cnyToEur(sourceCnyMinor));
    const serviceLines = [...pricing.querySelectorAll('[data-jj-service-line]')].map((line, index) => ({
      line,
      index,
      kind: line.dataset.jjServiceLineKind || 'route',
      weight: safeMinor(line.dataset.jjServiceLineEurMinor),
      output: line.querySelector('[data-jj-service-line-money]'),
    })).filter((entry) => entry.weight !== null && entry.output);
    const routeServiceLines = serviceLines.filter((entry) => entry.kind !== 'coordination');
    const coordinationLine = serviceLines.find((entry) => entry.kind === 'coordination') || null;

    let profileConfig = null;
    try {
      profileConfig = profileConfigElement ? JSON.parse(profileConfigElement.textContent || '') : null;
    } catch (_) {
      profileConfig = null;
    }

    const profileForDestination = () => {
      const profiles = profileConfig?.profiles;
      if (!profiles || typeof profiles !== 'object') return null;
      const country = String(destinationControl?.value || 'ES').toUpperCase();
      const postal = String(destinationPostalControl?.value || '').toUpperCase().replaceAll(' ', '');
      const remote = (country === 'ES' && /^(35|38|51|52)/.test(postal))
        || (country === 'PT' && /^9/.test(postal))
        || (country === 'FR' && /^(20|971|972|973|974|975|976|977|978|984|986|987|988)/.test(postal))
        || (country === 'IT' && /^(07|08|09|90|91|92|93|94|95|96|97|98)/.test(postal));
      if (remote && profiles.EU_ISLAND_REMOTE) return ['EU_ISLAND_REMOTE', profiles.EU_ISLAND_REMOTE];
      const match = Object.entries(profiles).find(([, profile]) => (
        Array.isArray(profile?.countries) && profile.countries.includes(country)
      ));
      const profileId = match?.[0] || profileConfig.defaultProfile || 'ES';
      return [profileId, profiles[profileId] || profiles.ES || null];
    };

    const applyServiceProfile = () => {
      const resolved = profileForDestination();
      if (!resolved) return;
      const [profileId, profile] = resolved;
      const amounts = Array.isArray(profile?.amounts) ? profile.amounts.map((amount) => Number(amount)) : [];
      const destinationTotal = Number(profile?.destinationTotalEurMinor);
      const valid = Number.isSafeInteger(destinationTotal) && destinationTotal >= 0
        && amounts.length === routeServiceLines.length
        && amounts.every((amount) => Number.isSafeInteger(amount) && amount >= 0)
        && amounts.reduce((sum, amount) => sum + amount, 0) === destinationTotal;
      if (!valid) return;
      routeServiceLines.forEach((entry, index) => {
        entry.weight = amounts[index];
        entry.line.dataset.jjServiceLineEurMinor = String(amounts[index]);
      });
      pricing.dataset.jjServiceProfileId = profileId;
      pricing.dataset.jjServiceProfileVersion = String(profileConfig.profileVersion || '');
      pricing.dataset.jjDestinationServiceEurMinor = String(destinationTotal);
      const breakdown = pricing.querySelector('[data-jj-service-breakdown]');
      if (breakdown) {
        breakdown.dataset.jjServiceProfileId = profileId;
        breakdown.dataset.jjServiceProfileVersion = String(profileConfig.profileVersion || '');
        breakdown.dataset.jjDestinationServiceEurMinor = String(destinationTotal);
      }
      if (routeScopeOutput) routeScopeOutput.textContent = String(profile.scope || profileId);
      if (routeReviewOutput) routeReviewOutput.hidden = profile.manual !== true;
    };

    const inCurrency = (eurAmount, currency, exactCnyAmount = null) => {
      if (currency === 'EUR') return eurAmount;
      if (currency === 'USD') return eurAmount === null ? null : eurToUsd(eurAmount);
      if (currency === 'CNY') {
        if (exactCnyAmount !== null) return exactCnyAmount;
        return eurAmount === null ? null : eurToCny(eurAmount);
      }
      return null;
    };

    const pricingScope = pricing.closest('.jj-vehicle-product') || pricing;
    const setMoney = (role, amount, currency) => {
      const outputs = pricingScope.querySelectorAll(`[data-jj-money-role="${role}"]`);
      const formatted = amount === null ? null : formatMinor(amount, currency);
      if (formatted) outputs.forEach((output) => { output.textContent = formatted; });
    };

    const setServiceBreakdown = (routeServiceTotal, coordinationFee, currency) => {
      if (routeServiceTotal === null || !routeServiceLines.length) return;
      const weightTotal = routeServiceLines.reduce((sum, entry) => sum + entry.weight, 0);
      if (weightTotal <= 0) return;
      const weighted = routeServiceLines.map((entry) => {
        const numerator = routeServiceTotal * entry.weight;
        return {
          ...entry,
          amount: Math.floor(numerator / weightTotal),
          remainder: numerator % weightTotal,
        };
      });
      let remainder = routeServiceTotal - weighted.reduce((sum, entry) => sum + entry.amount, 0);
      const order = [...weighted].sort((left, right) => (
        left.remainder === right.remainder
          ? left.index - right.index
          : right.remainder - left.remainder
      ));
      for (let cursor = 0; remainder > 0; cursor += 1, remainder -= 1) {
        order[cursor % order.length].amount += 1;
      }
      weighted.forEach((entry) => {
        entry.output.textContent = formatMinor(entry.amount, currency);
      });
      if (coordinationLine && coordinationFee !== null) {
        coordinationLine.output.textContent = formatMinor(coordinationFee, currency);
        coordinationLine.line.dataset.jjServiceLineDisplayMinor = String(coordinationFee);
      }
      const breakdown = pricing.querySelector('[data-jj-service-breakdown]');
      if (breakdown) {
        const serviceTotal = coordinationFee === null ? routeServiceTotal : routeServiceTotal + coordinationFee;
        breakdown.dataset.jjDestinationServiceDisplayMinor = String(routeServiceTotal);
        breakdown.dataset.jjCoordinationDisplayMinor = coordinationFee === null ? '' : String(coordinationFee);
        breakdown.dataset.jjServiceDisplayMinor = String(serviceTotal);
        breakdown.dataset.jjServiceLinesDisplayMinor = String(
          weighted.reduce((sum, entry) => sum + entry.amount, 0) + (coordinationFee ?? 0),
        );
        breakdown.dataset.jjServiceDisplayCurrency = currency;
      }
    };

    const updatePricing = () => {
      applyServiceProfile();
      const currency = SUPPORTED_DISPLAY_CURRENCIES.has(currencyControl.value)
        ? currencyControl.value
        : 'EUR';
      currencyControl.value = currency;
      rememberCurrency(currency);

      const source = inCurrency(sourceEur, currency, sourceCnyMinor);
      const destinationServiceEurMinor = safeMinor(pricing.dataset.jjDestinationServiceEurMinor);
      const routeService = inCurrency(destinationServiceEurMinor, currency);
      const tax = inCurrency(taxEurMinor, currency);
      const subtotalBeforeFee = source !== null && routeService !== null && tax !== null
        ? source + routeService + tax
        : null;
      const solveFinalTotal = (subtotal) => {
        if (!Number.isSafeInteger(subtotal) || subtotal < 0) return null;
        let total = Math.max(0, Math.floor((subtotal * 10000) / 9700) - 2);
        while (Number.isSafeInteger(total)) {
          const fee = Math.floor(((total * 300) + 5000) / 10000);
          if (total - fee === subtotal) return { total, fee };
          total += 1;
        }
        return null;
      };
      const solved = subtotalBeforeFee === null ? null : solveFinalTotal(subtotalBeforeFee);
      const coordination = solved?.fee ?? null;
      const service = routeService === null || coordination === null
        ? null
        : routeService + coordination;
      const authoritativeTotal = inCurrency(totalEurMinor, currency);
      const calculatedTotal = solved?.total ?? null;
      // Keep the displayed breakdown arithmetically exact in the selected
      // currency. The server-side formal quote still owns the binding total.
      const total = calculatedTotal ?? authoritativeTotal;
      const first = total === null ? null : Math.floor(((total * 30) + 50) / 100);
      const final = total === null || first === null ? null : total - first;

      setMoney('source', source, currency);
      setMoney('route-service', routeService, currency);
      setMoney('coordination', coordination, currency);
      setMoney('service', service, currency);
      setServiceBreakdown(routeService, coordination, currency);
      setMoney('tax', tax, currency);
      setMoney('total', total, currency);
      setMoney('first', first, currency);
      setMoney('final', final, currency);
    };

    currencyControl.value = preferredCurrency();
    currencyControl.addEventListener('change', updatePricing);
    destinationControl?.addEventListener('change', updatePricing);
    destinationPostalControl?.addEventListener('input', updatePricing);
    destinationPostalControl?.addEventListener('change', updatePricing);
    updatePricing();

    const mobileQuoteBar = pricingScope.querySelector('[data-jj-mobile-quote-bar]');
    if (mobileQuoteBar && 'IntersectionObserver' in window) {
      const quoteVisibility = new IntersectionObserver((entries) => {
        entries.forEach((entry) => mobileQuoteBar.classList.toggle('is-obscured', entry.isIntersecting));
      }, {threshold: 0.12});
      quoteVisibility.observe(pricing);
    }
  });

  const installLiquidButton = (button) => {
    if (!(button instanceof HTMLElement) || button.dataset.jjLiquidButtonReady === 'true') return;
    // Financial, quotation, account and contract actions must keep a stable
    // hit-area and silhouette on every mobile compositor.  The liquid surface
    // is therefore opt-in and decorative only; normal JJ buttons retain their
    // native background and border-radius.
    const supportsLiquidSurface = button.matches('[data-jj-liquid-decorative="true"].jj-button--dark, [data-jj-liquid-decorative="true"].jj-button--blue, [data-jj-liquid-decorative="true"].jj-button--light')
      && window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 901px)').matches;
    if (!supportsLiquidSurface) return;
    button.dataset.jjLiquidButtonReady = 'true';

    const namespace = 'http://www.w3.org/2000/svg';
    const surface = document.createElementNS(namespace, 'svg');
    const group = document.createElementNS(namespace, 'g');
    const mass = document.createElementNS(namespace, 'rect');
    const drop = document.createElementNS(namespace, 'circle');
    surface.classList.add('jj-liquid-button__surface');
    surface.setAttribute('aria-hidden', 'true');
    surface.setAttribute('focusable', 'false');
    group.setAttribute('filter', 'url(#jj-liquid-gooey-filter)');
    mass.classList.add('jj-liquid-button__mass');
    drop.classList.add('jj-liquid-button__drop');
    group.append(mass, drop);
    surface.append(group);
    button.prepend(surface);

    const size = () => {
      const width = button.clientWidth;
      const height = button.clientHeight;
      if (width <= 0 || height <= 0) return;
      const radius = Math.min(10, Math.max(7, height * 0.24));
      surface.setAttribute('viewBox', `0 0 ${width} ${height}`);
      surface.setAttribute('preserveAspectRatio', 'none');
      mass.setAttribute('x', '0');
      mass.setAttribute('y', '0');
      mass.setAttribute('width', String(width));
      mass.setAttribute('height', String(height));
      mass.setAttribute('rx', String(height / 2));
      drop.setAttribute('cx', String(width - (radius * 0.7)));
      drop.setAttribute('cy', String(height / 2));
      drop.setAttribute('r', String(radius));
    };

    size();
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(size);
      resizeObserver.observe(button);
    }
  };

  document.querySelectorAll('.jj-button').forEach(installLiquidButton);
  if ('MutationObserver' in window) {
    const liquidButtonObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('.jj-button')) installLiquidButton(node);
          node.querySelectorAll?.('.jj-button').forEach(installLiquidButton);
        });
      });
    });
    liquidButtonObserver.observe(document.body, {childList: true, subtree: true});
  }

  const installLiquidCluster = (cluster) => {
    if (!(cluster instanceof HTMLElement) || cluster.dataset.jjLiquidReady === 'true') return;
    cluster.dataset.jjLiquidReady = 'true';
    cluster.classList.add('jj-liquid-cluster');

    const namespace = 'http://www.w3.org/2000/svg';
    const surface = document.createElementNS(namespace, 'svg');
    const group = document.createElementNS(namespace, 'g');
    const mass = document.createElementNS(namespace, 'rect');
    const drop = document.createElementNS(namespace, 'circle');
    surface.classList.add('jj-liquid-cluster__surface');
    surface.setAttribute('aria-hidden', 'true');
    surface.setAttribute('focusable', 'false');
    group.setAttribute('filter', 'url(#jj-liquid-gooey-filter)');
    mass.classList.add('jj-liquid-cluster__mass');
    mass.setAttribute('x', '0');
    mass.setAttribute('y', '0');
    mass.setAttribute('width', '1');
    mass.setAttribute('height', '1');
    mass.setAttribute('rx', '.5');
    drop.classList.add('jj-liquid-cluster__drop');
    drop.setAttribute('cx', '.5');
    drop.setAttribute('cy', '.5');
    drop.setAttribute('r', '.5');
    group.append(mass, drop);
    surface.append(group);
    cluster.prepend(surface);

    let items = [];
    let lastCenter = null;
    let settleTimer = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const readItems = () => {
      items = [...cluster.querySelectorAll('a,button')].filter((item) => !item.closest('[hidden]'));
      return items;
    };

    const defaultTarget = () => readItems().find((item) => {
      const ariaCurrent = item.getAttribute('aria-current');
      return (
        (Boolean(ariaCurrent) && ariaCurrent !== 'false')
        || item.classList.contains('is-active')
        || item.classList.contains('selected')
      );
    }) || items.find((item) => !item.disabled) || items[0];

    const place = (target, animate = true) => {
      if (!(target instanceof HTMLElement) || !cluster.contains(target)) return;
      const clusterRect = cluster.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (clusterRect.width <= 0 || targetRect.width <= 0 || targetRect.height <= 0) {
        surface.hidden = true;
        return;
      }
      surface.hidden = false;
      const x = targetRect.left - clusterRect.left;
      const y = targetRect.top - clusterRect.top;
      const width = targetRect.width;
      const height = targetRect.height;
      const center = {x: x + width / 2, y: y + height / 2};
      const diameter = Math.min(28, Math.max(14, height * 0.64));
      mass.style.transform = `matrix(${width},0,0,${height},${x},${y})`;

      const dropAt = (point) => {
        drop.style.transform = `matrix(${diameter},0,0,${diameter},${point.x - diameter / 2},${point.y - diameter / 2})`;
      };
      const spatialMotion = animate && !reduceMotion.matches && lastCenter;
      if (spatialMotion) {
        dropAt(lastCenter);
        cluster.classList.add('is-moving');
        window.requestAnimationFrame(() => dropAt(center));
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => cluster.classList.remove('is-moving'), 360);
      } else {
        dropAt(center);
        cluster.classList.remove('is-moving');
      }
      lastCenter = center;
    };

    const reset = (animate = true) => {
      const target = defaultTarget();
      if (target) place(target, animate);
    };

    cluster.addEventListener('pointerover', (event) => {
      const target = event.target instanceof Element ? event.target.closest('a,button') : null;
      if (target && cluster.contains(target)) place(target);
    });
    cluster.addEventListener('pointerleave', () => reset());
    cluster.addEventListener('focusin', (event) => {
      const target = event.target instanceof Element ? event.target.closest('a,button') : null;
      if (target && cluster.contains(target)) place(target);
    });
    cluster.addEventListener('focusout', () => {
      window.requestAnimationFrame(() => {
        if (!cluster.contains(document.activeElement)) reset();
      });
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => reset(false));
      resizeObserver.observe(cluster);
    }
    const mutationObserver = new MutationObserver((records) => {
      const externalStateChanged = records.some((record) => (
        record.type === 'childList'
        || record.attributeName !== 'class'
        || record.target !== cluster
      ));
      if (externalStateChanged) reset(false);
    });
    mutationObserver.observe(cluster, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-current', 'class', 'hidden']});
    reset(false);
  };

  const installSectionNavigationState = (navigation) => {
    if (!(navigation instanceof HTMLElement) || navigation.dataset.jjSectionNavigationReady === 'true') return;
    const links = [...navigation.querySelectorAll('[data-jj-section-nav-link]')];
    const targets = new Map(
      [...document.querySelectorAll('[data-jj-section-nav-target]')]
        .map((target) => [target.dataset.jjSectionNavTarget, target])
        .filter(([key, target]) => key && target instanceof HTMLElement),
    );
    if (!links.length || !targets.size) return;
    navigation.dataset.jjSectionNavigationReady = 'true';
    let suppressObserverUntil = 0;

    const setCurrent = (currentLink) => {
      if (!(currentLink instanceof HTMLAnchorElement)) return;
      links.forEach((link) => {
        if (link === currentLink) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const currentFromHash = () => links.find((link) => link.hash && link.hash === window.location.hash);
    setCurrent(currentFromHash() || links.find((link) => link.hasAttribute('aria-current')) || links[0]);

    links.forEach((link) => {
      link.addEventListener('click', () => {
        suppressObserverUntil = Date.now() + 650;
        setCurrent(link);
      });
    });
    window.addEventListener('hashchange', () => {
      const hashLink = currentFromHash();
      if (hashLink) setCurrent(hashLink);
    });

    if (!('IntersectionObserver' in window)) return;
    const updateFromViewport = () => {
      if (Date.now() < suppressObserverUntil) return;
      const anchorY = Math.min(window.innerHeight * 0.34, 320);
      const ranked = links
        .map((link) => {
          const target = targets.get(link.dataset.jjSectionNavLink);
          if (!(target instanceof HTMLElement)) return null;
          const rect = target.getBoundingClientRect();
          const containsAnchor = rect.top <= anchorY && rect.bottom >= anchorY;
          const distance = containsAnchor ? 0 : Math.min(Math.abs(rect.top - anchorY), Math.abs(rect.bottom - anchorY));
          return {link, distance};
        })
        .filter(Boolean)
        .sort((left, right) => left.distance - right.distance);
      if (ranked[0]) setCurrent(ranked[0].link);
    };
    const sectionObserver = new IntersectionObserver(updateFromViewport, {
      rootMargin: '-16% 0px -58% 0px',
      threshold: [0, 0.01, 0.25],
    });
    targets.forEach((target) => sectionObserver.observe(target));
    window.requestAnimationFrame(updateFromViewport);
  };

  document.querySelectorAll('.jj-vehicle-product__section-nav').forEach(installSectionNavigationState);

  document.querySelectorAll('.jj-vehicle-product__section-nav').forEach(installLiquidCluster);

  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('[data-reveal]').forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );

  document.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element));
})();
